import { range } from 'lodash';
import {
    deleteLayerGroups,
    restoreLayerGroups,
    deleteLayerGroupStudents,
    addLayerGroupStudents,
    getLayerGroupStudents,
    updateCourseLayer,
    removeCourseLayer,
    restoreCourseLayer,
    uploadCourseLayersTemplate,
    getCourseLayer,
    createLayerGroup,
    getLayerGroups,
    deleteManyLayerGroups,
    restoreManyLayerGroups,
} from '@/api/layersApi.js';
import useSnackbar from '@/hooks/snackbars.js';
import undoIcon from '@/assets/svg/icons-backward-arrow.svg';
import { isAxiosError } from '@/utils/http.js';
import httpStatusCodes from '@/utils/httpStatusCodes.js';
import { getGroup } from '@/api/groupApi.js';
import { createCourseStudentsReport } from '@/api/studentApi.js';
import { getErrorMsg } from '@/utils/helpers.js';

const { createSnackbar } = useSnackbar();

const createDefaultGroupStudents = () => ({
    loading: false,
    students: null,
    page: 1,
    meta: {},
    open: false,
});

const errorHandler = (error, actionName, errorName) => {
    const loggingErrorName = `${actionName}: ${errorName}`;

    console.error(loggingErrorName, error);

    throw error;
};

export const getIsGroupPartialSelected = ({ group }) => Boolean(group.students.length || group.exceptStudents.length);

export default {
    namespaced: true,
    state: {
        data: {},
        loading: true,
        loadingGroups: true,
        page: 1,
        searchQuery: '',
        studentsByGroup: {},
        selectedGroups: [],
        addStudents: {
            groupId: null,
        },
    },
    getters: {
        layer: state => state.data.layer,
        groups: state => state.data.groups,
        meta: state => state.data.meta,
        loading: state => state.loading,
        loadingGroups: state => state.loadingGroups,
        page: state => state.page,
        searchQuery: state => state.searchQuery,

        distributedStudentsCount: state => state.data.layer.distributed_students_count,
        notDistributedStudentsCount: state => state.data.layer.not_distributed_students_count,

        getGroupStudentsById: state => groupId => state.studentsByGroup[groupId],

        selectedGroups: state => state.selectedGroups,
        getSelectedGroupById: state => groupId => state.selectedGroups.find(group => group.id === groupId),
    },
    mutations: {
        setData(state, data) {
            state.data = data || {};
        },
        setLayer(state, layer) {
            state.data.layer = layer;
        },
        setGroups(state, groups) {
            groups.forEach(group => {
                if (!state.studentsByGroup[group.id]) state.studentsByGroup[group.id] = createDefaultGroupStudents();
            });

            state.data.groups = groups;
        },
        setMeta(state, meta) {
            state.data.meta = meta;
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        setLoadingGroups(state, loading) {
            state.loadingGroups = loading;
        },
        setPage(state, page) {
            state.page = page;
        },
        setSearchQuery(state, searchQuery) {
            state.searchQuery = searchQuery;
        },
        setGroupStudentsCount(state, { count, groupId }) {
            state.data.groups = state.data.groups.map(group => (group.id === groupId
                ? {
                    ...group,
                    students_count: count,
                }
                : group));
        },
        patchGroup(state, { group }) {
            state.data.groups = state.data.groups.map(item => {
                if (group.id === item.id) {
                    return group;
                }

                return item;
            });
        },

        resetGroupStudents(state) {
            Object.keys(state.studentsByGroup).forEach(groupId => {
                state.studentsByGroup[groupId] = createDefaultGroupStudents();
            });
        },
        setGroupStudents: (state, { students, groupId }) => {
            state.studentsByGroup[groupId].students = students;
        },
        setGroupStudentsPage(state, { groupId, page }) {
            state.studentsByGroup[groupId].page = page;
        },
        setGroupStudentsOpen(state, { groupId, open }) {
            state.studentsByGroup[groupId].open = open;
        },
        setGroupStudentsLoading(state, { groupId, loading }) {
            state.studentsByGroup[groupId].loading = loading;
        },
        setGroupStudentsMeta(state, { groupId, meta }) {
            state.studentsByGroup[groupId].meta = meta;
        },
        showAddStudentsToGroup(state, payload) {
            state.addStudents.groupId = payload.groupId;
        },
        hideAddStudentsToGroup(state) {
            state.addStudents.groupId = null;
        },
        setSelectedGroups(state, selectedGroups) {
            state.selectedGroups = selectedGroups;
        },
        setSelectedGroupData(state, { groupId, data }) {
            const groupIndex = state.selectedGroups.findIndex(group => group.id === groupId);

            if (groupIndex === -1) {
                return;
            }

            state.selectedGroups[groupIndex] = {
                ...state.selectedGroups[groupIndex],
                ...data,
            };
        },
    },
    actions: {
        resetLayerState({ commit }) {
            commit('setLoading', false);
            commit('setData', null);
            commit('resetGroupStudents');
            commit('setPage', 1);
            commit('setSearchQuery', '');
            commit('setSelectedGroups', []);
        },
        async loadLayer({ commit }, { courseId, layerId }) {
            try {
                commit('setLoading', true);

                const { data } = await getCourseLayer({
                    courseId,
                    layerId,
                });

                commit('setLayer', data.data);

            } finally {
                commit('setLoading', false);
            }
        },
        async loadGroups({ commit, dispatch, getters }, { courseId, layerId, page, query, appendContent }) {
            try {
                commit('setLoadingGroups', true);

                const { data } = await getLayerGroups({
                    courseId,
                    layerId,
                    query: query || null,
                    page,
                });

                commit('setGroups', appendContent ? [...getters.groups, ...data.data] : data.data);
                commit('setMeta', data.meta);

                dispatch('refetchOpenedGroupItemStudents', {
                    courseId,
                    layerId,
                });
            } finally {
                commit('setLoadingGroups', false);
            }
        },
        async updateLayer({ commit }, { courseId, layerId, newTitle }) {
            try {
                const { data } = await updateCourseLayer({
                    courseId,
                    layerId,
                    layer: { title: newTitle },
                });

                commit('setLayer', data.data);

                createSnackbar({
                    type: 'success',
                    message: 'Новое название сохранено',
                });
            } catch (error) {
                if (isAxiosError(error) && error.response && error.response.status === httpStatusCodes.UNPROCESSABLE_ENTITY && error.response.data.error) {
                    createSnackbar({
                        type: 'error',
                        message: error.response.data.error,
                    });
                } else {
                    createSnackbar({
                        type: 'error',
                        message: 'Ошибка. Попробуйте еще раз',
                    });
                }

                errorHandler(error, 'updateLayer', 'Не удалось обновить слой');
            }
        },
        async restoreLayer(_, { courseId, layerId }) {
            try {
                await restoreCourseLayer({
                    courseId,
                    layerId,
                });
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'restoreLayer', 'Не удалось восстановить слой');
            }
        },
        async deleteLayer({ dispatch }, { courseId, layerId }) {
            try {
                await removeCourseLayer({
                    courseId,
                    layerId,
                });

                createSnackbar({
                    type: 'timer',
                    message: 'Слой удален',
                    actionButton: {
                        iconInline: undoIcon,
                        text: 'Отменить',
                        on: {
                            click: async () => {
                                await dispatch('restoreLayer', {
                                    courseId,
                                    layerId,
                                });

                                await dispatch('layers/fetchLayers', { courseId }, { root: true });
                            },
                        },
                    },
                });
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'deleteLayer', 'Не удалось удалить слой');
            }
        },
        async loadGroupStudents({ commit, getters }, { groupId, courseId, layerId, appendData, page }) {
            try {
                commit('setGroupStudentsLoading', {
                    groupId,
                    loading: true,
                });

                const { data } = await getLayerGroupStudents({
                    groupId,
                    courseId,
                    layerId,
                    page,
                });

                commit('setGroupStudents', {
                    groupId,
                    students: appendData ? [...getters.getGroupStudentsById(groupId).students, ...data.data] : data.data,
                });
                commit('setGroupStudentsMeta', {
                    groupId,
                    meta: data.meta,
                });
                commit('setGroupStudentsCount', {
                    groupId,
                    count: data.meta.total,
                });
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'loadGroupStudents', 'Не удалось загрузить студентов группы');
            } finally {
                commit('setGroupStudentsLoading', {
                    groupId,
                    loading: false,
                });
            }
        },
        async restoreGroups({ dispatch }, { courseId, layerId, groups }) {
            try {
                await restoreLayerGroups({
                    courseId,
                    layerId,
                    groups,
                });

                dispatch('resetFilters');

                await Promise.all([
                    dispatch('refetchLayer', {
                        courseId,
                        layerId,
                    }),
                    dispatch('loadGroups', {
                        courseId,
                        layerId,
                    }),
                ]);
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'restoreGroups', 'Не удалось восстановить группы');
            }
        },
        async deleteGroups({ dispatch, getters, commit }, { courseId, layerId, groups }) {
            try {
                await deleteLayerGroups({
                    courseId,
                    layerId,
                    groups,
                });

                commit('setSelectedGroups', getters.selectedGroups.filter(group => !groups.includes(group.id)));

                await Promise.all([
                    dispatch('refetchLayer', {
                        courseId,
                        layerId,
                    }),
                    dispatch('loadGroups', {
                        courseId,
                        layerId,
                        page: getters.page,
                        query: getters.searchQuery,
                    }),
                ]);

                createSnackbar({
                    type: 'timer',
                    message: groups.length > 1 ? 'Группы удалены' : 'Группа удалена',
                    actionButton: {
                        iconInline: undoIcon,
                        text: 'Отменить',
                        on: {
                            click: () => {
                                dispatch('restoreGroups', {
                                    courseId,
                                    layerId,
                                    groups,
                                });
                            },
                        },
                    },
                });
            } catch (error) {
                const isNotFoundError = isAxiosError(error) && error.response?.status === httpStatusCodes.NOT_FOUND;

                if (isNotFoundError) {
                    createSnackbar({
                        type: 'timer',
                        message: 'Слой удален',
                        actionButton: {
                            iconInline: undoIcon,
                            text: 'Отменить',
                            on: {
                                click: async () => {
                                    await dispatch('restoreGroups', {
                                        courseId,
                                        layerId,
                                        groups,
                                    });

                                    await dispatch('layers/fetchLayers', { courseId }, { root: true });
                                },
                            },
                        },
                    });
                } else {
                    createSnackbar({
                        type: 'error',
                        message: 'Ошибка. Попробуйте еще раз',
                    });
                }

                errorHandler(error, 'deleteGroups', 'Не удалось удалить группы');
            }
        },
        async createGroup(_, { courseId, layerId }) {
            try {
                await createLayerGroup({
                    courseId,
                    layerId,
                });
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'createGroup', 'Не удалось создать группу');
            }
        },
        async addStudents(_, { courseId, layerId, groupId, students }) {
            try {
                await addLayerGroupStudents({
                    courseId,
                    layerId,
                    groupId,
                    students,
                });
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'addStudents', 'Не удалось добавить студентов');
            }
        },
        async deleteStudents({ dispatch, commit, getters }, { courseId, layerId, groupId, students }) {
            try {
                await deleteLayerGroupStudents({
                    courseId,
                    groupId,
                    layerId,
                    students,
                });

                const selectedGroup = getters.getSelectedGroupById(groupId);

                if (selectedGroup) {
                    const withSelectedStudents = selectedGroup.students.length;

                    commit('setSelectedGroupData', {
                        groupId,
                        data: {
                            students: selectedGroup.students.filter(id => !students.includes(id)),
                            exceptStudents: selectedGroup.exceptStudents.filter(id => !students.includes(id)),
                        },
                    });

                    const isAllSelectedStudentsDeleted = !getters.getSelectedGroupById(groupId).students.length;

                    if (withSelectedStudents && isAllSelectedStudentsDeleted) {
                        commit('setSelectedGroups', getters.selectedGroups.filter(group => group.id !== groupId));
                    }
                }

                dispatch('refetchLayer', {
                    courseId,
                    layerId,
                });
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'deleteStudents', 'Не удалось удалить студентов');
            }
        },
        toggleGroupSelection({ commit, getters }, { groupId }) {
            const groupSelection = getters.getSelectedGroupById(groupId);

            if (!groupSelection) {
                commit('setSelectedGroups', [
                    ...getters.selectedGroups,
                    {
                        id: groupId,
                        students: [],
                        exceptStudents: [],
                    },
                ]);

                return;
            }

            const isPartialSelected = getIsGroupPartialSelected({ group: groupSelection });

            if (isPartialSelected) {
                commit('setSelectedGroupData', {
                    groupId,
                    data: {
                        students: [],
                        exceptStudents: [],
                    },
                });
            } else {
                commit('setSelectedGroups', getters.selectedGroups.filter(group => group.id !== groupId));
            }
        },
        toggleStudentSelection({ commit, getters }, { studentId, groupId }) {
            const groupStudentsCount = getters.groups.find(group => group.id === groupId)?.students_count || 0;
            const groupSelection = getters.getSelectedGroupById(groupId);

            if (!groupSelection) {
                commit('setSelectedGroups', [
                    ...getters.selectedGroups, {
                        id: groupId,
                        students: [studentId],
                        exceptStudents: [],
                    },
                ]);

                return;
            }

            if (groupSelection.students.length) {
                const isStudentSelected = groupSelection.students.includes(studentId);

                if (isStudentSelected) {
                    commit('setSelectedGroupData', {
                        groupId,
                        data: {
                            students: groupSelection.students.filter(id => id !== studentId),
                        },
                    });

                    const isStudentsEmpty = !getters.getSelectedGroupById(groupId)?.students.length;

                    if (isStudentsEmpty) {
                        commit('setSelectedGroups', getters.selectedGroups.filter(group => group.id !== groupId));
                    }
                } else {
                    commit('setSelectedGroupData', {
                        groupId,
                        data: {
                            students: [...groupSelection.students, studentId],
                        },
                    });
                }

                return;
            }

            const isStudentExcluded = groupSelection.exceptStudents.includes(studentId);

            if (isStudentExcluded) {
                commit('setSelectedGroupData', {
                    groupId,
                    data: {
                        exceptStudents: groupSelection.exceptStudents.filter(id => id !== studentId),
                    },
                });
            } else {
                commit('setSelectedGroupData', {
                    groupId,
                    data: {
                        exceptStudents: [...groupSelection.exceptStudents, studentId],
                    },
                });

                const isAllUsersExcluded = getters.getSelectedGroupById(groupId)?.exceptStudents.length === groupStudentsCount;

                if (isAllUsersExcluded) {
                    commit('setSelectedGroups', getters.selectedGroups.filter(group => group.id !== groupId));
                }
            }
        },
        async deleteManyGroups({ getters, commit, dispatch }, { courseId, layerId }) {
            let restoreToken = null;

            try {
                const { data } = await deleteManyLayerGroups({
                    courseId,
                    layerId,
                    groups: getters.selectedGroups.map(group => ({
                        ...group,
                        except_students: group.exceptStudents,
                    })),
                });

                restoreToken = data.data.restore_token;

                const notPartialSelectedGroupsCount = getters.selectedGroups.filter(group => !getIsGroupPartialSelected({ group })).length;

                commit('setSelectedGroups', []);
                dispatch('resetFilters');

                await Promise.all([
                    dispatch('refetchLayer', {
                        courseId,
                        layerId,
                    }),
                    dispatch('loadGroups', {
                        courseId,
                        layerId,
                    }),
                ]);

                if (notPartialSelectedGroupsCount) {
                    createSnackbar({
                        type: 'timer',
                        message: notPartialSelectedGroupsCount > 1 ? 'Группы удалены' : 'Группа удалена',
                        actionButton: {
                            iconInline: undoIcon,
                            text: 'Отменить',
                            on: {
                                click: () => {
                                    dispatch('restoreManyGroups', {
                                        courseId,
                                        layerId,
                                        restoreToken,
                                    });
                                },
                            },
                        },
                    });
                }
            } catch (error) {
                const isNotFoundError = isAxiosError(error) && error.response?.status === httpStatusCodes.NOT_FOUND;

                if (isNotFoundError) {
                    createSnackbar({
                        type: 'timer',
                        message: 'Слой удален',
                        actionButton: {
                            iconInline: undoIcon,
                            text: 'Отменить',
                            on: {
                                click: async () => {
                                    await dispatch('restoreManyGroups', {
                                        courseId,
                                        layerId,
                                        restoreToken,
                                    });

                                    await dispatch('layers/fetchLayers', { courseId }, { root: true });
                                },
                            },
                        },
                    });
                } else {
                    createSnackbar({
                        type: 'error',
                        message: 'Ошибка. Попробуйте еще раз',
                    });
                }

                errorHandler(error, 'deleteManyGroups', 'Не удалось массово удалить группы');
            }
        },
        async restoreManyGroups({ dispatch }, { restoreToken, courseId, layerId }) {
            try {
                await restoreManyLayerGroups({
                    courseId,
                    layerId,
                    restore_token: restoreToken,
                });

                await Promise.all([
                    dispatch('refetchLayer', {
                        courseId,
                        layerId,
                    }),
                    dispatch('loadGroups', {
                        courseId,
                        layerId,
                    }),
                ]);
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'restoreManyGroups', 'Не удалось массово восстановить группы');
            }
        },
        async moveStudent({ dispatch, commit }, { from, to, studentId, layerId, courseId }) {
            if (from === to) {
                return;
            }

            await dispatch('addStudents', {
                groupId: to,
                layerId,
                courseId,
                students: [studentId],
            });

            commit('setGroupStudentsPage', {
                groupId: from,
                page: 1,
            });
            commit('setGroupStudentsPage', {
                groupId: to,
                page: 1,
            });

            await Promise.all([
                dispatch('loadGroupStudents', {
                    groupId: from,
                    courseId,
                    layerId,
                    page: 1,
                }),
                dispatch('loadGroupStudents', {
                    groupId: to,
                    courseId,
                    layerId,
                    page: 1,
                }),
            ]);
        },
        async uploadLayersTemplate(_, { courseId, file } = {}) {
            try {
                await uploadCourseLayersTemplate({
                    courseId,
                    file,
                });
            } catch (error) {
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });

                errorHandler(error, 'uploadLayersTemplate', 'Не удалось загрузить шаблон');
            }
        },
        resetFilters({ commit }) {
            commit('setPage', 1);
            commit('setSearchQuery', '');
        },
        async refetchLayer({ commit }, payload) {
            const { courseId, layerId } = payload;
            const { data: layerData } = await getCourseLayer({
                courseId,
                layerId,
            });

            commit('setLayer', layerData.data);
        },
        async refetchLayerGroupItem({ commit }, payload) {
            const { groupId, courseId } = payload;

            const { data: groupResp } = await getGroup({
                groupId,
                courseId,
            });

            commit('patchGroup', { group: groupResp.data });
        },
        async refetchGroupItemStudents({ commit, getters }, payload) {
            const { courseId, layerId, groupId } = payload;

            const groupStudentData = getters.getGroupStudentsById(groupId);

            if (groupStudentData?.open) {
                const pagesForLoad = range(1, groupStudentData.page + 1);

                const pagesResponse = pagesForLoad.map(page => getLayerGroupStudents({
                    groupId,
                    courseId,
                    layerId,
                    page,
                }));
                const results = await Promise.all(pagesResponse);

                if (!results.length) return;

                const { meta } = results[0].data;
                const students = results.flatMap(resp => resp.data.data);

                commit('setGroupStudents', {
                    groupId,
                    students,
                });
                commit('setGroupStudentsMeta', {
                    groupId,
                    meta,
                });
                commit('setGroupStudentsCount', {
                    groupId,
                    count: meta.total,
                });
            }
        },
        refetchOpenedGroupItemStudents({ getters, dispatch }, { courseId, layerId }) {
            for (const group of getters.groups) {
                const groupStudentsData = getters.getGroupStudentsById(group.id);

                if (groupStudentsData?.open) {
                    dispatch('refetchGroupItemStudents', {
                        groupId: group.id,
                        courseId,
                        layerId,
                    });
                }
            }
        },
        async refetchLayerGroupItemWithStudents({ dispatch }, payload) {
            await Promise.all([
                dispatch('refetchLayer', payload),
                dispatch('refetchLayerGroupItem', payload),
                dispatch('refetchGroupItemStudents', payload),
            ]);
        },
        async createLayerStudentsReport(_, { courseId, developmentCenterId }) {
            try {
                const { data } = await createCourseStudentsReport({ courseId });

                if (data.success === false) {
                    throw data;
                }

                createSnackbar({
                    type: 'info',
                    size: 'large',
                    title: 'Отчет добавлен в очередь на обработку',
                    message: 'По завершению обработки, отчет можно скачать на странице отчетов',
                    link: {
                        text: 'Перейти к отчетам',
                        route: {
                            name: 'AdminReports',
                            params: {
                                developmentCenterId,
                            },
                        },
                    },
                });
            } catch (e) {
                console.error(e);
                createSnackbar({
                    type: 'error',
                    size: 'large',
                    title: getErrorMsg(e, 'Ошибка импорта. Попробуйте еще раз'),
                });
            }
        },
    },
};
